// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import getNumberTextField from "@store/Framework/Common/Fields/Toolkit/numberText";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionValue,
} from "@toolkit/Framework/Common/Utilities/dataOptions";
import getFullBreakpointValues from "@toolkit/Framework/Common/Utilities/getFullBreakpointValues";
import { getGridPreset } from "@toolkit/Framework/Common/Utilities/gridPreset";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import * as cssLinter from "css";
import React, { FC, useMemo } from "react";
import { css, useTheme } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import Carousel, { CarouselPresentationProps } from "@store/Overrides/Components/Carousel";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import {
    CarouselButtonPresentationProps,
    carouselButtonStyles,
} from "@store/Library/Components/Toolkit/Carousel/CarouselButton";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

export type LayoutOption = DataOption<GridItemProps["width"]>;
export interface LayoutOptionMapper {
    custom: LayoutOption;
    fourColumn: LayoutOption;
    threeColumn: LayoutOption;
    twoColumn: LayoutOption;
}
export type LayoutOptions = DataOptions<LayoutOption> & LayoutOptionMapper;
export type Layout = keyof LayoutOptionMapper;

export const layoutOptions: LayoutOptions = {
    twoColumn: { id: "twoColumn", displayName: "2 Columns", value: [12, 6, 6, 6, 6] },
    threeColumn: { id: "threeColumn", displayName: "3 Columns", value: [12, 6, 4, 4, 4], isDefault: true },
    fourColumn: { id: "fourColumn", displayName: "4 Columns", value: [12, 6, 4, 4, 3] },
    custom: { id: "custom", displayName: "Custom" },
};

const mobileBreakpoint: BreakpointKey = "xs";
type MobileCarouselArrowsPlacement = "sides" | "topRight";

const enum fields {
    customCSS = "customCSS",
    customLayout = "customLayout",
    gap = "gap",
    layout = "layout",
    maxWidth = "maxWidth",
    tiles = "tiles",
    title = "title",
    titleColor = "titleColor",
    mobileCarousel = "mobileCarousel",
    mobileCarouselPeekNextSlide = "mobileCarouselPeekNextSlide",
    mobileCarouselShowArrows = "mobileCarouselShowArrows",
    mobileCarouselArrowsPlacement = "mobileCarouselArrowsPlacement",
}

const enum tileFields {
    id = "id",
    title = "title",
}

const customLayoutField = getNumberTextField(fields.customLayout, { min: 1, max: 12 });
const gapField = getCssUnitField(fields.gap, { shorthands: ["row", "column"] });
const maxWidthField = getCssUnitField(fields.maxWidth);
const mobileControlsOffset = -40;
const mobileControlsReservedWidth = 80;

export interface QuickLinksSectionTileProps {
    fields: {
        [tileFields.id]: string;
        [tileFields.title]: string;
    };
}

export interface QuickLinksSectionProps extends WidgetProps {
    fields: {
        [fields.customCSS]: string;
        [fields.customLayout]: string;
        [fields.gap]: string;
        [fields.layout]: string;
        [fields.maxWidth]: string;
        [fields.tiles]: QuickLinksSectionTileProps[];
        [fields.title]: string;
        [fields.titleColor]: string;
        [fields.mobileCarousel]: boolean;
        [fields.mobileCarouselPeekNextSlide]: boolean;
        [fields.mobileCarouselShowArrows]: boolean;
        [fields.mobileCarouselArrowsPlacement]: MobileCarouselArrowsPlacement;
    };
}

export interface QuickLinksSectionStyles {
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    title?: TypographyPresentationProps;
    titleMobile?: TypographyPresentationProps;
    carousel?: CarouselPresentationProps;
}

const arrowButtonStyles: CarouselButtonPresentationProps = {
    extendedStyles: mergeProps(carouselButtonStyles, {
        button: {
            css: css`
                background-color: ${getColor("common.backgroundContrast")};
                transition: ${transitionHoverStyles("background-color, color")};

                &:not(:disabled) {
                    color: ${getColor("primary.contrast")};

                    &:hover,
                    &:active {
                        background-color: ${getColor("common.background")};
                        color: ${getColor("secondary.main")};
                    }
                }

                &:disabled {
                    color: ${getColor("button.disabled.contrast")};
                    background-color: ${getColor("common.disabledContrast")};
                }
            `,
        },
    }),
};

const quickLinksSectionStyles: QuickLinksSectionStyles = {
    gridItem: {
        css: css`
            justify-content: space-between;
            align-items: stretch;
        `,
    },
    title: {
        variant: "h2",
        css: css`
            color: var(--quick-links-title-color);
        `,
    },
    titleMobile: {
        variant: "h2",
        css: css`
            display: flex;
            align-items: center;
            color: var(--quick-links-title-color);
        `,
    },
    carousel: {
        showDots: false,
        itemsPerSlide: 1,
        itemsToScroll: 1,
        loop: false,
        extendedStyles: {
            carouselArrowNext: {
                extendedStyles: { carouselButton: arrowButtonStyles },
            },
            carouselArrowPrevious: {
                extendedStyles: { carouselButton: arrowButtonStyles },
            },
        },
    },
};

const styles = quickLinksSectionStyles;

const QuickLinksSection: FC<QuickLinksSectionProps> = ({ fields, id }) => {
    const theme = useTheme();
    const tilesSafe = fields.tiles ?? [];
    const isMobile = useIsBreakpoint(mobileBreakpoint, "down");
    const hasTitle = Boolean(fields.title?.trim());
    const isInMobileCarouselMode = Boolean(fields.mobileCarousel && isMobile);

    const wrapperCss = useMemo(
        () => css`
            ${breakpointValues(maxWidthField.parse(fields.maxWidth), "max-width")}
            --quick-links-title-color: ${fields.titleColor ? resolveColor(fields.titleColor, theme) : getColor("text")};
            ${fields.customCSS}
        `,
        [fields.customCSS, fields.maxWidth, fields.titleColor, theme],
    );

    const gridContainerStyles = useMemo(() => {
        const gridPreset = gapField.parseToGridPreset(fields.gap);

        return mergeProps<QuickLinksSectionStyles["gridContainer"]>(
            styles.gridContainer,
            mergeProps<QuickLinksSectionStyles["gridContainer"]>(gridPreset ? getGridPreset(gridPreset) : undefined, {
                gap: 0,
            }),
        );
    }, [fields.gap]);

    const gridItemStyles = useMemo(() => {
        return mergeProps<QuickLinksSectionStyles["gridItem"]>(styles.gridItem, {
            width:
                (fields.layout === layoutOptions.custom.id
                    ? (getFullBreakpointValues(
                          customLayoutField.parse(fields.customLayout),
                          theme.breakpoints.keys.length,
                      ) as GridItemProps["width"])
                    : getDataOptionValue(layoutOptions, fields.layout)) || 12,
        });
    }, [fields.customLayout, fields.layout, theme.breakpoints.keys.length]);

    const isTopRightArrows = fields.mobileCarouselArrowsPlacement === "topRight";
    const shouldReserveSpaceForTopRightArrows =
        isInMobileCarouselMode && fields.mobileCarouselShowArrows && isTopRightArrows;

    const titleMobileStyles = useMemo(() => {
        if (!isInMobileCarouselMode) return styles.titleMobile;

        return mergeProps<TypographyPresentationProps>(styles.titleMobile, {
            css: css`
                max-width: ${shouldReserveSpaceForTopRightArrows
                    ? `calc(100% - ${pxToRem(mobileControlsReservedWidth)})`
                    : "100%"};
            `,
        });
    }, [isInMobileCarouselMode, shouldReserveSpaceForTopRightArrows]);

    const carouselStyles = useMemo(() => {
        const base = mergeProps<CarouselPresentationProps>(styles.carousel, {
            peekNextSlide: fields.mobileCarouselPeekNextSlide,
            showArrows: fields.mobileCarouselShowArrows,
            inlineArrowsBreakpoint: fields.mobileCarouselShowArrows && isTopRightArrows ? mobileBreakpoint : undefined,
        });

        return mergeProps<CarouselPresentationProps>(base, {
            extendedStyles: {
                carouselSlidesContainer: {
                    css: css`
                        display: flex;
                        align-items: stretch;
                    `,
                },
                carouselSlide: {
                    css: css`
                        height: auto;
                        display: flex;
                        align-items: stretch;

                        > div {
                            height: 100%;
                            width: 100%;
                            display: flex;

                            > .wrapper {
                                height: 100%;
                                width: 100%;
                            }
                        }
                    `,
                },
                ...(shouldReserveSpaceForTopRightArrows
                    ? {
                          carouselControls: {
                              css: css`
                                  position: absolute;
                                  top: ${pxToRem(mobileControlsOffset)};
                                  right: 0;
                                  margin-top: 0;
                                  z-index: 2;
                                  gap: 0.5rem;
                                  align-items: center;
                              `,
                          },
                      }
                    : {}),
            },
        });
    }, [
        fields.mobileCarouselPeekNextSlide,
        fields.mobileCarouselShowArrows,
        isTopRightArrows,
        shouldReserveSpaceForTopRightArrows,
    ]);

    if (!tilesSafe.length) {
        return null;
    }

    if (isInMobileCarouselMode) {
        const slides = tilesSafe.map(({ fields: tile }, index) => {
            const zoneKey = tile.id || `${index}`;
            return {
                id: zoneKey,
                renderComponent: () => <Zone contentId={id} zoneName={`Tile-${zoneKey}`} />,
            };
        });

        return (
            <StyledWrapper
                css={css`
                    ${wrapperCss}
                    position: relative;
                `}
            >
                {hasTitle && <Typography {...titleMobileStyles}>{fields.title}</Typography>}
                <Carousel id={`quick-links-carousel-${id}`} slides={slides} {...carouselStyles} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper css={wrapperCss}>
            {hasTitle && <Typography {...styles.title}>{fields.title}</Typography>}
            <GridContainer {...gridContainerStyles}>
                {tilesSafe.map(({ fields: tile }, index) => {
                    const zoneKey = tile.id || `${index}`;
                    return (
                        <GridItem key={zoneKey} {...gridItemStyles} className="tile">
                            <Zone contentId={id} zoneName={`Tile-${zoneKey}`} />
                        </GridItem>
                    );
                })}
            </GridContainer>
        </StyledWrapper>
    );
};

const defaultCustomCss = `& {

}`;

const mobileTab = {
    displayName: "Mobile",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: QuickLinksSection,
    definition: {
        group: "Basic",
        icon: "border-all",
        displayName: "Quick Links Section",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.titleColor,
                displayName: "Title Color",
                editorTemplate: "CustomColorPickerField",
                fieldType: "Contextual",
                defaultValue: "",
            },
            {
                ...getDataOptionsFieldDefinition(layoutOptions),
                name: fields.layout,
                fieldType: "Contextual",
                hideEmptyOption: true,
            },
            {
                ...customLayoutField.definition,
                fieldType: "Contextual",
                isVisible: x => x.fields.layout === layoutOptions.custom.id,
            },
            {
                ...gapField.definition,
                displayName: "Gap Between Tiles",
                fieldType: "Contextual",
            },
            {
                name: fields.mobileCarousel,
                displayName: "Use Carousel on Mobile",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                tab: mobileTab,
            },
            {
                name: fields.mobileCarouselShowArrows,
                displayName: "Show Arrows",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                tab: mobileTab,
                isVisible: item => !!item.fields[fields.mobileCarousel],
            },
            {
                name: fields.mobileCarouselArrowsPlacement,
                displayName: "Arrows Placement",
                editorTemplate: "DropDownField",
                fieldType: "Contextual",
                tab: mobileTab,
                defaultValue: "topRight",
                hideEmptyOption: true,
                isVisible: item =>
                    !!item.fields[fields.mobileCarousel] && !!item.fields[fields.mobileCarouselShowArrows],
                options: [
                    { value: "topRight", displayName: "Top Right" },
                    { value: "sides", displayName: "Sides" },
                ],
                tooltip: "Top Right places the arrows on the same row as the Title.",
            },
            {
                name: fields.mobileCarouselPeekNextSlide,
                displayName: "Peek Next Slide",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                tab: mobileTab,
                isVisible: item => !!item.fields[fields.mobileCarousel],
                tooltip:
                    "When enabled, you will see part of the next slide instead of a single full-screen tile on mobile.",
            },
            {
                name: fields.tiles,
                editorTemplate: "ListField",
                getDisplay: x => x.fields.title || translate("Tile"),
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: tileFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: tileFields.title,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                ...maxWidthField.definition,
                fieldType: "Contextual",
                tab: advancedTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
